import { useEffect } from "react";

/* Lightweight per-route SEO/AEO updater.
   Updates <title>, <meta name="description">, <meta property="og:title">,
   <meta property="og:description"> and canonical link when a page mounts.
   No external dependency — pure DOM manipulation.
   For app-only / authenticated pages, also adds <meta name="robots" content="noindex,nofollow">. */
export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  noindex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;
    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        const parts = selector.replace(/[[\]"=]/g, " ").split(/\s+/).filter(Boolean);
        // crude parser — e.g. meta[name="description"]
        if (parts[0] === "meta") {
          if (parts.includes("name")) el.setAttribute("name", parts[parts.indexOf("name") + 1]);
          if (parts.includes("property")) el.setAttribute("property", parts[parts.indexOf("property") + 1]);
        } else if (parts[0] === "link") {
          el.setAttribute("rel", parts[parts.indexOf("rel") + 1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    if (description) {
      set('meta[name="description"]', "content", description);
      set('meta[property="og:description"]', "content", description);
      set('meta[name="twitter:description"]', "content", description);
    }
    if (title) {
      set('meta[property="og:title"]', "content", title);
      set('meta[name="twitter:title"]', "content", title);
    }
    if (canonical) set('link[rel="canonical"]', "href", canonical);
    if (ogImage) {
      set('meta[property="og:image"]', "content", ogImage);
      set('meta[name="twitter:image"]', "content", ogImage);
    }
    set('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
  }, [title, description, canonical, ogImage, noindex]);
  return null;
}
